import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Calendar, User, Heart, MessageCircle, Share2, ChevronRight, X, Loader2, ArrowLeft } from 'lucide-react';

const Blog = ({ selectedPostId, wordpressUrl = 'https://dimensionias.com/cms', blogCategoryId = null, blogCategorySlug = 'blogs' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [postsPerPage] = useState(6);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Fetch categories from WordPress (only blog-related categories)
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${wordpressUrl}/wp-json/wp/v2/categories?per_page=100`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      
      const wpCategories = await response.json();
      
      // If specific blog category ID is provided, get its children
      if (blogCategoryId) {
        const blogCategories = wpCategories.filter(cat => 
          cat.parent === blogCategoryId || cat.id === blogCategoryId
        );
        const categoryNames = blogCategories
          .filter(cat => cat.count > 0)
          .map(cat => cat.name);
        setCategories(['All', ...categoryNames]);
      } else {
        // Look for blog-related categories by name/slug
        const blogCategories = wpCategories.filter(cat => {
          const categoryName = cat.name.toLowerCase();
          const categorySlug = cat.slug.toLowerCase();
          
          // Include categories that contain "blog" or match specific blog terms
          return (categoryName.includes('blog') || 
                  categorySlug.includes('blog') ||
                  categorySlug === blogCategorySlug) && cat.count > 0;
        });
        
        if (blogCategories.length > 0) {
          const categoryNames = blogCategories.map(cat => cat.name);
          setCategories(['All', ...categoryNames]);
        } else {
          // Fallback: exclude known non-blog categories
          const excludedTerms = ['current', 'affair', 'gallery', 'image', 'photo', 'media', 'video', 'news'];
          const filteredCategories = wpCategories.filter(cat => {
            const categoryName = cat.name.toLowerCase();
            return cat.count > 0 && 
                   !excludedTerms.some(term => categoryName.includes(term));
          });
          const categoryNames = filteredCategories.map(cat => cat.name);
          setCategories(['All', ...categoryNames]);
        }
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories(['All', 'Blog']); // Fallback categories
    }
  }, [wordpressUrl, blogCategoryId, blogCategorySlug]);

  // Transform WordPress post data
  const transformPost = useCallback((post) => {
    const featuredImage = post._embedded?.['wp:featuredmedia']?.[0]?.source_url || 
                         post._embedded?.['wp:featuredmedia']?.[0]?.media_details?.sizes?.medium?.source_url || 
                         '/images/blogs/default.jpg';
    
    const author = post._embedded?.author?.[0]?.name || 'Admin';
    const postCategories = post._embedded?.['wp:term']?.[0] || [];
    const primaryCategory = postCategories.length > 0 ? postCategories[0].name : 'Blog';
    
    const wordCount = post.content.rendered.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const readTime = Math.max(1, Math.ceil(wordCount / 200));

    return {
      id: post.id,
      slug: post.slug,
      title: post.title.rendered,
      excerpt: post.excerpt.rendered.replace(/<[^>]*>/g, '').replace(/\[&hellip;\]/, '...').trim() || 
               post.content.rendered.replace(/<[^>]*>/g, '').substring(0, 150) + '...',
      content: post.content.rendered,
      author: author,
      date: post.date,
      image: featuredImage,
      category: primaryCategory,
      likes: Math.floor(Math.random() * 100) + 10,
      comments: post.comment_count || 0,
      readTime: `${readTime} min read`,
      wpPost: post
    };
  }, []);

  // Fetch posts from WordPress (only blog posts)
  const fetchPosts = useCallback(async (page = 1, category = 'All') => {
    try {
      setLoading(true);
      setError(null);

      let apiUrl = `${wordpressUrl}/wp-json/wp/v2/posts?per_page=${postsPerPage}&page=${page}&_embed=true`;
      
      // Method 1: If specific blog category ID is provided
      if (blogCategoryId) {
        if (category === 'All') {
          // Get all posts from blog category and its children
          const categoriesResponse = await fetch(`${wordpressUrl}/wp-json/wp/v2/categories?parent=${blogCategoryId}&per_page=100`);
          if (categoriesResponse.ok) {
            const childCategories = await categoriesResponse.json();
            const allBlogCategoryIds = [blogCategoryId, ...childCategories.map(cat => cat.id)];
            apiUrl += `&categories=${allBlogCategoryIds.join(',')}`;
          } else {
            apiUrl += `&categories=${blogCategoryId}`;
          }
        } else {
          // Get specific category
          const categoryResponse = await fetch(`${wordpressUrl}/wp-json/wp/v2/categories?slug=${category.toLowerCase().replace(/\s+/g, '-')}`);
          if (categoryResponse.ok) {
            const categoryData = await categoryResponse.json();
            if (categoryData.length > 0) {
              apiUrl += `&categories=${categoryData[0].id}`;
            }
          }
        }
      }
      // Method 2: Filter by blog category slug/name
      else if (category === 'All') {
        const categoriesResponse = await fetch(`${wordpressUrl}/wp-json/wp/v2/categories?per_page=100`);
        if (categoriesResponse.ok) {
          const allCategories = await categoriesResponse.json();
          
          // Find blog-related categories
          const blogCategories = allCategories.filter(cat => {
            const categoryName = cat.name.toLowerCase();
            const categorySlug = cat.slug.toLowerCase();
            return categoryName.includes('blog') || 
                   categorySlug.includes('blog') ||
                   categorySlug === blogCategorySlug;
          });
          
          if (blogCategories.length > 0) {
            const blogCategoryIds = blogCategories.map(cat => cat.id);
            apiUrl += `&categories=${blogCategoryIds.join(',')}`;
          } else {
            // Fallback: exclude non-blog categories
            const excludedTerms = ['current', 'affair', 'gallery', 'image', 'photo', 'media', 'video', 'news'];
            const excludedCategoryIds = allCategories
              .filter(cat => excludedTerms.some(term => cat.name.toLowerCase().includes(term)))
              .map(cat => cat.id);
            
            if (excludedCategoryIds.length > 0) {
              apiUrl += `&categories_exclude=${excludedCategoryIds.join(',')}`;
            }
          }
        }
      } else {
        // Specific category selected
        const categoryResponse = await fetch(`${wordpressUrl}/wp-json/wp/v2/categories?slug=${category.toLowerCase().replace(/\s+/g, '-')}`);
        if (categoryResponse.ok) {
          const categoryData = await categoryResponse.json();
          if (categoryData.length > 0) {
            apiUrl += `&categories=${categoryData[0].id}`;
          }
        }
      }

      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error('Failed to fetch posts');

      const wpPosts = await response.json();
      const total = parseInt(response.headers.get('X-WP-Total') || '0');
      setTotalPosts(total);

      // Transform WordPress posts
      const transformedPosts = wpPosts.map(transformPost);

      if (page === 1) {
        setBlogPosts(transformedPosts);
      } else {
        setBlogPosts(prev => [...prev, ...transformedPosts]);
      }

      setCurrentPage(page);

    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load blog posts. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [wordpressUrl, postsPerPage, blogCategoryId, blogCategorySlug, transformPost]);

  // Search posts from WordPress (only blog posts)
  const searchPosts = useCallback(async (query) => {
    if (!query.trim()) {
      fetchPosts(1, selectedCategory);
      return;
    }

    try {
      setLoading(true);
      
      let apiUrl = `${wordpressUrl}/wp-json/wp/v2/posts?search=${encodeURIComponent(query)}&per_page=50&_embed=true`;
      
      // Apply same filtering logic as fetchPosts
      if (blogCategoryId) {
        const categoriesResponse = await fetch(`${wordpressUrl}/wp-json/wp/v2/categories?parent=${blogCategoryId}&per_page=100`);
        if (categoriesResponse.ok) {
          const childCategories = await categoriesResponse.json();
          const allBlogCategoryIds = [blogCategoryId, ...childCategories.map(cat => cat.id)];
          apiUrl += `&categories=${allBlogCategoryIds.join(',')}`;
        } else {
          apiUrl += `&categories=${blogCategoryId}`;
        }
      } else {
        const categoriesResponse = await fetch(`${wordpressUrl}/wp-json/wp/v2/categories?per_page=100`);
        if (categoriesResponse.ok) {
          const allCategories = await categoriesResponse.json();
          
          const blogCategories = allCategories.filter(cat => {
            const categoryName = cat.name.toLowerCase();
            const categorySlug = cat.slug.toLowerCase();
            return categoryName.includes('blog') || 
                   categorySlug.includes('blog') ||
                   categorySlug === blogCategorySlug;
          });
          
          if (blogCategories.length > 0) {
            const blogCategoryIds = blogCategories.map(cat => cat.id);
            apiUrl += `&categories=${blogCategoryIds.join(',')}`;
          }
        }
      }
      
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error('Search failed');

      const wpPosts = await response.json();
      const transformedPosts = wpPosts.map(transformPost);

      setBlogPosts(transformedPosts);
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [wordpressUrl, blogCategoryId, blogCategorySlug, selectedCategory, fetchPosts, transformPost]);

  // Fetch specific post by ID or slug
  const fetchPostById = useCallback(async (postId) => {
    try {
      const response = await fetch(`${wordpressUrl}/wp-json/wp/v2/posts/${postId}?_embed=true`);
      if (!response.ok) throw new Error('Post not found');

      const post = await response.json();
      return transformPost(post);
    } catch (err) {
      console.error('Error fetching post:', err);
      return null;
    }
  }, [wordpressUrl, transformPost]);

  // Initialize data on component mount
  useEffect(() => {
    fetchCategories();
    fetchPosts();
  }, [fetchCategories, fetchPosts]);

  // Handle search with debouncing - FIXED: Clear previous timeout properly
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        searchPosts(searchTerm);
      } else {
        fetchPosts(1, selectedCategory);
      }
    }, 500);

    setSearchTimeout(timeoutId);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [searchTerm]); // Removed dependencies that cause unnecessary re-renders

  // Handle category change - FIXED: Only trigger when not searching
  useEffect(() => {
    if (!searchTerm) {
      fetchPosts(1, selectedCategory);
    }
  }, [selectedCategory, fetchPosts, searchTerm]);

  // Handle scroll to show/hide scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Open specific post if selectedPostId is provided
  useEffect(() => {
    if (selectedPostId) {
      fetchPostById(selectedPostId).then(post => {
        if (post) {
          openPost(post);
        }
      });
    }
  }, [selectedPostId, fetchPostById]);

  // Memoize filtered posts to prevent unnecessary re-renders
  const filteredPosts = useMemo(() => blogPosts, [blogPosts]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLike = useCallback(async (postId) => {
    // Update local state immediately for better UX
    setBlogPosts(posts => 
      posts.map(post => 
        post.id === postId 
          ? { ...post, likes: post.likes + 1 }
          : post
      )
    );

    // Here you could also send a request to your WordPress backend
    // to store likes in a custom field or plugin
    try {
      // Example API call to store likes (you'd need a custom endpoint)
      // await fetch(`${wordpressUrl}/wp-json/custom/v1/posts/${postId}/like`, {
      //   method: 'POST'
      // });
    } catch (err) {
      console.error('Error updating like:', err);
    }
  }, []);

  const openPost = useCallback((post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closePost = useCallback(() => {
    setSelectedPost(null);
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  // Load more posts
  const loadMorePosts = useCallback(() => {
    fetchPosts(currentPage + 1, selectedCategory);
  }, [fetchPosts, currentPage, selectedCategory]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        closePost();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModalOpen, closePost]);

  if (error && blogPosts.length === 0) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0FDF7' }}>
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <button 
            onClick={() => fetchPosts()}
            className="px-6 py-3 rounded-lg text-white font-semibold transition-colors"
            style={{ backgroundColor: '#134E4A' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2DD4BF'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#134E4A'}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 sm:pt-24 min-h-screen" style={{ backgroundColor: '#F0FDF7', color: '#134E4A' }}>
      {/* Search and Filter Section */}
      <section className="py-6 sm:py-8 bg-white shadow-sm mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 border-b" style={{ borderColor: '#E0F2F1' }}>
        <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-md w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search articles..."
              className="w-full pl-12 pr-4 py-3 rounded-full placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-opacity-70 border border-transparent text-gray-700"
              style={{ 
                backgroundColor: 'rgba(224, 242, 241, 0.3)',
                '--tw-ring-color': '#2DD4BF'
              }}
              onFocus={e => {
                e.currentTarget.style.borderColor = '#2DD4BF';
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(45, 212, 191, 0.2)';
              }}
              onBlur={e => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === category
                    ? 'text-white shadow-lg'
                    : 'bg-white border hover:bg-opacity-90'
                }`}
                style={{
                  backgroundColor: selectedCategory === category ? '#134E4A' : 'white',
                  color: selectedCategory === category ? 'white' : '#134E4A',
                  borderColor: selectedCategory === category ? '#134E4A' : '#E0F2F1'
                }}
                onMouseEnter={e => {
                  if (selectedCategory !== category) {
                    e.currentTarget.style.borderColor = '#2DD4BF';
                    e.currentTarget.style.color = '#2DD4BF';
                    e.currentTarget.style.backgroundColor = 'rgba(224, 242, 241, 0.3)';
                  }
                }}
                onMouseLeave={e => {
                  if (selectedCategory !== category) {
                    e.currentTarget.style.borderColor = '#E0F2F1';
                    e.currentTarget.style.color = '#134E4A';
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-8 sm:py-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading && blogPosts.length === 0 ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="animate-spin mr-2" style={{ color: '#2DD4BF' }} size={32} />
            <span className="text-lg" style={{ color: '#134E4A' }}>Loading articles...</span>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map(post => (
                <article 
                  key={`${post.id}-${post.slug}`} 
                  className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden cursor-pointer border-2 group"
                  style={{ borderColor: '#E0F2F1' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = '#2DD4BF';
                    e.currentTarget.style.backgroundColor = '#FEFFFE';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = '#E0F2F1';
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <div className="relative overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/blogs/default.jpg';
                      }}
                    />
                    <div className="absolute top-4 left-4">
                      <span 
                        className="text-white px-3 py-1 rounded-full text-sm font-medium select-none"
                        style={{ backgroundColor: '#134E4A' }}
                      >
                        {post.category}
                      </span>
                    </div>
                    <div 
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{ 
                        background: 'linear-gradient(to top, rgba(45, 212, 191, 0.8), rgba(19, 78, 74, 0.3))' 
                      }}
                    />
                  </div>
                  
                  <div className="p-6">
                    <h3 
                      onClick={() => openPost(post)}
                      className="text-xl font-bold mb-3 line-clamp-2 transition-colors cursor-pointer group-hover:text-teal-500"
                      style={{ color: '#134E4A' }}
                    >
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <User size={16} />
                          <span>{post.author}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar size={16} />
                          <span>{new Date(post.date).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <span className="font-medium" style={{ color: '#2DD4BF' }}>{post.readTime}</span>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: '#E0F2F1' }}>
                      <div className="flex items-center space-x-4">
                       
                      </div>
                      <button 
                        onClick={() => openPost(post)}
                        className="flex items-center space-x-1 font-medium transition-colors"
                        style={{ color: '#2DD4BF' }}
                        onMouseEnter={e => e.currentTarget.style.color = '#134E4A'}
                        onMouseLeave={e => e.currentTarget.style.color = '#2DD4BF'}
                      >
                        <span>Read More</span>
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Load More Button */}
            {!searchTerm && totalPosts > blogPosts.length && (
              <div className="text-center mt-12">
                <button
                  onClick={loadMorePosts}
                  disabled={loading}
                  className="px-8 py-3 rounded-lg text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 mx-auto"
                  style={{ backgroundColor: '#134E4A' }}
                  onMouseEnter={e => !loading && (e.currentTarget.style.backgroundColor = '#2DD4BF')}
                  onMouseLeave={e => !loading && (e.currentTarget.style.backgroundColor = '#134E4A')}
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <span>Load More Articles</span>
                  )}
                </button>
              </div>
            )}

            {filteredPosts.length === 0 && !loading && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  No articles found matching your search criteria.
                </p>
              </div>
            )}
          </>
        )}
      </section>

      {/* Full Page Article View */}
      {selectedPost && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto min-h-screen">
          {/* Top Bar Navigation */}
          <div className="bg-white border-b sticky top-0 z-30 shadow-sm">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
              <button
                onClick={closePost}
                className="flex items-center space-x-2 text-teal-900 font-semibold hover:text-teal-600 transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Back to All Articles</span>
              </button>
              <div className="text-sm text-gray-500 hidden sm:block truncate max-w-xs">
                {selectedPost.title}
              </div>
            </div>
          </div>

          {/* Hero Banner */}
          <div className="relative w-full bg-teal-900 text-white py-16 md:py-20">
            {selectedPost.image && (
              <div className="absolute inset-0 overflow-hidden opacity-25">
                <img 
                  src={selectedPost.image} 
                  alt={selectedPost.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/images/blogs/default.jpg';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-950 via-teal-900/80 to-teal-900/60" />
              </div>
            )}
            
            <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="mb-4">
                <span className="bg-teal-400 text-teal-950 px-4 py-1.5 rounded-full text-sm font-bold tracking-wide uppercase">
                  {selectedPost.category}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight mb-6">
                {selectedPost.title}
              </h1>
              <div className="flex flex-wrap items-center gap-6 text-teal-100 text-sm md:text-base border-t border-teal-800/60 pt-6">
                <div className="flex items-center space-x-2">
                  <User size={18} className="text-teal-300" />
                  <span className="font-medium">{selectedPost.author}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar size={18} className="text-teal-300" />
                  <span>{new Date(selectedPost.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span>⏱️ {selectedPost.readTime}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Full Page Content Body */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10 pb-24">
            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-10 md:p-12 border border-teal-50">
              {/* Gutenberg Content with Rich WordPress Formatting */}
              <div 
                dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                className="wordpress-content"
              />
              
              {/* Article Actions / Footer */}
              <div className="mt-12 pt-8 border-t border-teal-100 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <button 
                    onClick={() => handleLike(selectedPost.id)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-red-500 transition-colors bg-teal-50 hover:bg-red-50 px-5 py-2.5 rounded-full font-medium"
                  >
                    <Heart size={20} />
                    <span>{selectedPost.likes} Likes</span>
                  </button>
                  <button 
                    className="flex items-center space-x-2 text-gray-700 hover:text-teal-600 transition-colors bg-teal-50 hover:bg-teal-100 px-5 py-2.5 rounded-full font-medium"
                  >
                    <MessageCircle size={20} />
                    <span>{selectedPost.comments} Comments</span>
                  </button>
                </div>
                <button 
                  onClick={closePost}
                  className="flex items-center space-x-2 bg-teal-800 hover:bg-teal-700 text-white px-6 py-2.5 rounded-full font-medium shadow-md transition-all"
                >
                  <ArrowLeft size={18} />
                  <span>Back to Articles</span>
                </button>
              </div>
              
              {/* Author Bio Card */}
              <div className="mt-10 p-6 rounded-2xl bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-100 flex items-center space-x-5">
                <div className="w-16 h-16 rounded-full bg-teal-800 text-white flex items-center justify-center font-bold text-2xl shadow-md shrink-0">
                  {selectedPost.author.charAt(0)}
                </div>
                <div>
                  <h4 className="text-lg font-bold text-teal-900">{selectedPost.author}</h4>
                  <p className="text-sm text-teal-700 mt-0.5">Faculty & Content Contributor, Dimension IAS</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Published on {new Date(selectedPost.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-3 rounded-full shadow-lg transition-colors z-40"
          style={{ backgroundColor: '#134E4A' }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2DD4BF'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#134E4A'}
          aria-label="Scroll to top"
        >
          <ChevronRight size={24} className="transform -rotate-90 text-white" />
        </button>
      )}
    </div>
  );
};

export default Blog;